import { useEffect, useState, type FormEvent } from 'react';
import { doc } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocRow } from '@/lib/useDocs';
import { CompetencyPicker } from '@/components/CompetencyPicker';
import { CandidateConsentPage } from './CandidateConsentPage';
import { messageForAuthError } from '@/lib/authErrors';
import type { Candidate, Competency } from '@/types/domain';
import { strings } from '@/strings';

const updateCandidateProfile = callable<Record<string, unknown>, { candidateId: string }>('updateCandidateProfile');

/**
 * نمط التنفيذ is a MULTI-select on the form and includes ميداني. There is no
 * "any": someone open to everything ticks everything, which says the same thing
 * without a value that has to be interpreted.
 *
 * مرونة الوقت used to live here as a candidate attribute. It is a property of
 * the OPPORTUNITY — field 19 of بطاقة توصيف الفرصة — so it moved there.
 */
const MODES = [
  { value: 'onsite', label: strings.modeOnsite },
  { value: 'remote', label: strings.modeRemote },
  { value: 'hybrid', label: strings.modeHybrid },
  { value: 'field', label: strings.modeField },
] as const;

/**
 * Consent gates the screen, not just the data: someone who has not agreed sees
 * the agreement and nothing else, because a profile form implies the profile
 * is already in use.
 */
export function CandidateProfilePage() {
  const { claims } = useAuth();
  const candidateId = String(claims.candidateId || '');
  const { row, loading, error } = useDocRow<Candidate>(
    candidateId ? doc(db, 'candidates', candidateId) : null, [candidateId]
  );

  const [form, setForm] = useState<Candidate | null>(null);
  const [competencies, setCompetencies] = useState<Competency[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (row) {
      setForm(row);
      setCompetencies(row.competencies || []);
    }
  }, [row, reloadKey]);

  if (loading) return <p className="text-muted">{strings.loading}</p>;
  if (error || !row || !form) return <p role="alert" className="text-danger">{strings.loadFailed}</p>;

  if (row.consent?.withdrawnAt && !row.consent?.granted) {
    return (
      <div className="flex max-w-lg flex-col gap-6">
        <h1 className="text-xl font-bold text-primary">{strings.myProfileTitle}</h1>
        <p className="text-ink">{strings.consentWithdrawn2}</p>
      </div>
    );
  }

  if (!row.consent?.granted) {
    return (
      <CandidateConsentPage
        candidateId={candidateId}
        granted={false}
        onChanged={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  const current = form;

  async function onSave(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setMessage(null);
    try {
      await updateCandidateProfile({
        candidateId,
        availability: current.availability,
        interests: current.interests,
        modes: current.modes || [],
        competencies,
        bio: current.bio,
      });
      setMessage(strings.profileSaved);
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-lg flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.myProfileTitle}</h1>

      <form onSubmit={onSave} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm" htmlFor="bio">
          {strings.fieldBio}
          <textarea id="bio" rows={3} value={current.bio}
            onChange={(e) => setForm({ ...current, bio: e.target.value })}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>

        <label className="flex flex-col gap-1 text-sm" htmlFor="hours">
          {strings.fieldHours}
          <input id="hours" type="number" min={0} value={current.availability.hoursPerWeek}
            onChange={(e) => setForm({
              ...current,
              availability: { ...current.availability, hoursPerWeek: Number(e.target.value) },
            })}
            className="rounded-md border border-border-subtle px-3 py-2" />
        </label>

        <fieldset className="flex flex-col gap-1 text-sm">
          <legend className="mb-1">{strings.fieldModes}</legend>
          {MODES.map((m) => (
            <label key={m.value} className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={(current.modes || []).includes(m.value)}
                onChange={(e) => setForm({
                  ...current,
                  modes: e.target.checked
                    ? [...(current.modes || []), m.value]
                    : (current.modes || []).filter((x) => x !== m.value),
                })}
              />
              {m.label}
            </label>
          ))}
        </fieldset>

        <CompetencyPicker value={competencies} onChange={setCompetencies} />

        <div className="flex items-center gap-3">
          <button type="submit" disabled={busy}
            className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
            {strings.candidateSave}
          </button>
          {message ? <span className="text-sm text-success">{message}</span> : null}
          {problem ? <span role="alert" className="text-sm text-danger">{problem}</span> : null}
        </div>
      </form>

      <CandidateConsentPage candidateId={candidateId} granted onChanged={() => setReloadKey((k) => k + 1)} />
    </div>
  );
}
